import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { ArrowLeft, Check, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router'

import { isApproverOrHigherRole, isPrivilegedRole } from '@ankor-io/common/domain/iam/account'
import { Role } from '@ankor-io/common/types/account'
import { CharterStatus, type CharterSummary } from '@ankor-io/common/types/charter'
import type { Category } from '@ankor-io/common/types/transaction/category'
import { type TransactionPayload, TransactionStatus } from '@ankor-io/common/types/transaction/transaction'
import { getStatusColour, getStatusText } from '@ankor-io/common/util/enum-to-string/transaction'

import { useTransaction } from '@/hooks/useTransaction'
import { useUserContext } from '@/providers/user/useUserContext'
import { Badge } from '@/ui-components/Badge'
import { Button } from '@/ui-components/Button'
import { Heading } from '@/ui-components/Heading'
import Loading from '@/ui-components/Loading'
import { Select } from '@/ui-components/Select'
import Tabs, { TabContent, TabItem } from '@/ui-components/Tabs'
import FilesAndDocuments from '@/views/TransactionEdit/FilesAndDocuments'
import TransactionEditDetails from '@/views/TransactionEdit/TransactionEditDetails'
import AddCategoryModal from '@/views/components/AddCategoryModal'

const NUM_CHARTERS = 100
const SKIP = 0

const TransactionEditPage: React.FC = () => {
  const { user } = useUserContext()
  const navigate = useNavigate()
  const { transactionId } = useParams()
  const { getAccessToken } = useKindeAuth()
  const { transaction, setTransaction } = useTransaction()
  const { activeOrganizationId, activeConnectedAccountId, activeOrganizationRole } = useUserContext()

  const [isLoading, setIsLoading] = useState(true)
  const [categories, setCategories] = useState<Category[]>([])
  const [areCategoriesLoading, setAreCategoriesLoading] = useState(true)
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false)
  const [isActioningTransaction, setIsActioningTransaction] = useState(false)

  // Charter selection state
  const [charters, setCharters] = useState<CharterSummary[]>([])
  const [areChartersLoading, setAreChartersLoading] = useState<boolean>(true)
  const [selectedCharter, setSelectedCharter] = useState<{
    id: string
    name: string
    charterCurrencyCode: string
  } | null>(null)

  const [activeTab, setActiveTab] = useState<'expense-details' | 'files-and-documents' | 'approval-history'>(
    'expense-details',
  )

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)
        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/transaction/${transactionId}`,
          {
            headers: {
              Authorization: `Bearer ${await getAccessToken()}`,
            },
          },
        )

        if (!res.ok) {
          throw new Error(`Failed to fetch transaction: ${res.status}`)
        }

        const transactionData = (await res.json()) as TransactionPayload
        setTransaction(transactionData)
      } catch (error) {
        console.error('Error fetching transaction data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    if (transaction?.id === transactionId) {
      setIsLoading(false)
    } else {
      fetchData()
    }
  }, [getAccessToken, setTransaction, transaction?.id, transactionId, activeOrganizationId, activeConnectedAccountId])

  useEffect(() => {
    const abortController = new AbortController()

    const fetchCategories = async () => {
      try {
        setAreCategoriesLoading(true)
        const res = await fetch(`/api/pay/org/${activeOrganizationId}/categories`, {
          headers: {
            Authorization: `Bearer ${await getAccessToken()}`,
          },
          signal: abortController.signal,
        })

        if (res.ok) {
          const categories = await res.json()
          setCategories(categories)
        }
      } catch (error) {
        if (!(error instanceof Error && error.name === 'AbortError')) {
          console.error('Failed to fetch categories:', error)
          toast.error('Failed to fetch categories')
        }
      } finally {
        if (!abortController.signal.aborted) {
          setAreCategoriesLoading(false)
        }
      }
    }

    fetchCategories()

    return () => {
      abortController.abort()
    }
  }, [activeOrganizationId, getAccessToken])

  // Fetch charters for dropdown
  useEffect(() => {
    const fetchCharters = async () => {
      try {
        setAreChartersLoading(true)
        const query = new URLSearchParams()
        query.set('take', NUM_CHARTERS.toString())
        query.set('skip', SKIP.toString())

        // Remove cancelled and completed charters from search
        const allowedCharterStatuses = [CharterStatus.UPCOMING, CharterStatus.IN_PROGRESS, CharterStatus.SIGNING_OFF]
        query.set('status', allowedCharterStatuses.join(','))

        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charters/search?${query.toString()}`,
          {
            headers: { Authorization: `Bearer ${await getAccessToken()}` },
          },
        )

        if (!res.ok) {
          throw new Error('Failed to fetch charters')
        }

        const data = (await res.json()) as CharterSummary[]
        setCharters(data || [])
      } catch (error) {
        if (!(error instanceof Error && error.name === 'AbortError')) {
          console.error('Error fetching charters:', error)
          setCharters([])
        }
      } finally {
        setAreChartersLoading(false)
      }
    }

    fetchCharters()
  }, [activeOrganizationId, activeConnectedAccountId, getAccessToken])

  // Preselect if transaction already tagged to a charter
  useEffect(() => {
    if (transaction?.charter?.id && transaction.charter.name && transaction.charterCurrencyCode) {
      setSelectedCharter({
        id: transaction.charter.id,
        name: transaction.charter.name,
        charterCurrencyCode: transaction.charterCurrencyCode,
      })
    } else {
      setSelectedCharter(null)
    }
  }, [transaction?.charter, transaction?.charterCurrencyCode])

  const handleNewCategory = (category: Category) => {
    setCategories([...categories, category])
  }

  const handleApprove = (transaction: TransactionPayload | null) => {
    const approveTransaction = async () => {
      if (!transaction) {
        throw new Error('Transaction not found')
      }

      setIsActioningTransaction(true)

      try {
        // convert embarkationDate and disembarkationDate to ISO-8601 DateTime
        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/transaction/${transaction.id}/status`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await getAccessToken()}`,
            },
            method: 'POST',
            body: JSON.stringify({ status: TransactionStatus.APPROVED }),
          },
        )

        if (!res.ok) {
          throw new Error(`Failed to approve transaction.`)
        }

        const updatedTransaction = { ...transaction, status: TransactionStatus.APPROVED }
        setTransaction(updatedTransaction)
      } catch (error) {
        console.error('Error approving transactions:', error)
        throw new Error(`Failed to approve transaction.`)
      } finally {
        setIsActioningTransaction(false)
      }
    }

    toast.promise(approveTransaction, {
      loading: 'Approving expense...',
      success: 'Expense approved successfully.',
      error: 'Failed to approve expense.',
    })
  }

  const handleReject = (transaction: TransactionPayload | null) => {
    const rejectTransaction = async () => {
      if (!transaction) {
        throw new Error('Transaction not found')
      }

      setIsActioningTransaction(true)

      try {
        // convert embarkationDate and disembarkationDate to ISO-8601 DateTime
        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/transaction/${transaction.id}/status`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await getAccessToken()}`,
            },
            method: 'POST',
            body: JSON.stringify({ status: TransactionStatus.REJECTED }),
          },
        )

        if (!res.ok) {
          throw new Error(`Failed to reject transaction.`)
        }

        const updatedTransaction = { ...transaction, status: TransactionStatus.REJECTED }
        setTransaction(updatedTransaction)
      } catch (error) {
        console.error('Error rejecting transactions:', error)
        throw new Error(`Failed to reject transaction.`)
      } finally {
        setIsActioningTransaction(false)
      }
    }

    toast.promise(rejectTransaction, {
      loading: `Rejecting expense...`,
      success: `Expense rejected successfully.`,
      error: `Failed to reject expense.`,
    })
  }

  if (isLoading) {
    return <Loading />
  }

  if (!transaction) {
    return <div>Expense not found</div>
  }

  return (
    <>
      <Button
        plain
        className="mb-2"
        aria-label="Go back"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft data-slot="icon" />
        Back
      </Button>

      <div className="flex flex-col md:flex-row md:justify-between gap-2 mb-2">
        <div className="flex flex-col sm:flex-row sm:items-center gap-x-2">
          <Heading level={3}>Edit Expense</Heading>
          <Badge
            className="w-fit sm:w-auto h-fit"
            color={getStatusColour(transaction.status)}
          >
            {getStatusText(transaction.status)}
          </Badge>
        </div>

        {/* Actions */}
        {transaction?.status !== TransactionStatus.APPROVED &&
          transaction?.status !== TransactionStatus.REJECTED &&
          isApproverOrHigherRole(activeOrganizationRole) && (
            <div className="w-full md:w-auto flex md:justify-end items-center gap-x-2">
              <Button
                color="primary"
                className="disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto"
                disabled={isActioningTransaction}
                onClick={() => handleApprove(transaction)}
              >
                <Check data-slot="icon" />
                Approve
              </Button>
              <Button
                color="red"
                className="disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto"
                disabled={isActioningTransaction}
                onClick={() => handleReject(transaction)}
              >
                <X data-slot="icon" />
                Reject
              </Button>
            </div>
          )}
      </div>

      {/* Charter selector */}
      <div className="mb-4 max-w-sm">
        {areChartersLoading ? (
          <div className="mt-2">
            <Loading />
          </div>
        ) : (
          <div className="mt-2">
            <Select
              aria-label="Select charter"
              value={selectedCharter?.id ?? ''}
              onChange={(e) => {
                const id = e.target.value
                if (!id) {
                  setSelectedCharter(null)
                } else {
                  const found = charters.find((c) => c.id === id)
                  setSelectedCharter(
                    found ? { id: found.id, name: found.name, charterCurrencyCode: found.currencyCode } : null,
                  )
                }
              }}
            >
              <option value="">No charter selected</option>
              {charters.map((c) => (
                <option
                  key={c.id}
                  value={c.id}
                >
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
        )}
      </div>

      <Tabs>
        <TabItem
          current={activeTab === 'expense-details'}
          onClick={() => setActiveTab('expense-details')}
        >
          Expense Details
        </TabItem>
        <TabItem
          current={activeTab === 'files-and-documents'}
          onClick={() => setActiveTab('files-and-documents')}
        >
          Files and Documents
        </TabItem>
        <TabItem
          current={activeTab === 'approval-history'}
          onClick={() => setActiveTab('approval-history')}
        >
          Approval History
        </TabItem>
      </Tabs>

      <TabContent isActive={activeTab === 'expense-details'}>
        <TransactionEditDetails
          transaction={transaction}
          categories={categories}
          areCategoriesLoading={areCategoriesLoading}
          onOpenAddCategoryModal={() => setIsAddCategoryModalOpen(true)}
          selectedCharter={selectedCharter}
        />
      </TabContent>

      <TabContent isActive={activeTab === 'files-and-documents'}>
        <FilesAndDocuments
          transactionId={transaction.id}
          canDeleteAttachments={
            // Is Admin or Owner, can always action
            // Is Spender or Approver, can only delete attachments if the transaction is not approved
            isPrivilegedRole(activeOrganizationRole) ||
            ((activeOrganizationRole === Role.SPENDER || activeOrganizationRole === Role.APPROVER) &&
              transaction.createdBy === user?.id &&
              transaction.status !== TransactionStatus.APPROVED)
          }
        />
      </TabContent>

      <TabContent isActive={activeTab === 'approval-history'}>
        <div>Approval History</div>
      </TabContent>

      <AddCategoryModal
        isOpen={isAddCategoryModalOpen}
        onClose={() => setIsAddCategoryModalOpen(false)}
        onNewCategory={handleNewCategory}
      />
    </>
  )
}

export default TransactionEditPage
